import React, { useState, useEffect, useCallback, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FaChevronDown } from "react-icons/fa";
import StorageService from "../services/StorageService";
import { getAvailableRoles, getCurrentRole } from "../utils/roleSwitcher";
import {
  getSidebarSections,
  getSidebarIsActive,
  sidebarGroupHasActiveChild,
  sidebarSectionIsHighlighted,
} from "../config/sidebarNavigation";
import p2pNavigationService from "../services/p2pNavigation.service";

function SidebarNavLink({ item, pathname, search }) {
  const Icon = item.icon;
  const active = getSidebarIsActive(pathname, item, search);
  return (
    <NavLink
      to={item.to}
      end={item.end === true}
      className={() => `app-sidebar__link${active ? " app-sidebar__link--active" : ""}`}
    >
      <Icon className="app-sidebar__icon" aria-hidden />
      <span>{item.label}</span>
    </NavLink>
  );
}

/**
 * Collapsible module groups (RFQ, Invoice, Orders, etc.) — same behavior for every dashboard section.
 */
function SidebarCollapsibleGroups({ groups, pathname, search }) {
  const [openById, setOpenById] = useState({});
  const groupsRef = useRef(groups);
  groupsRef.current = groups;

  useEffect(() => {
    const gr = groupsRef.current;
    setOpenById((prev) => {
      const next = { ...prev };
      let changed = false;
      gr.forEach((g) => {
        if (sidebarGroupHasActiveChild(pathname, g.items, search)) {
          if (next[g.id] !== true) {
            next[g.id] = true;
            changed = true;
          }
        }
      });
      return changed ? next : prev;
    });
  }, [pathname, search]);

  const isGroupOpen = useCallback(
    (g) => {
      const m = openById[g.id];
      if (m === false) return false;
      if (m === true) return true;
      return sidebarGroupHasActiveChild(pathname, g.items, search);
    },
    [openById, pathname, search]
  );

  const toggle = useCallback((id) => {
    const g = groupsRef.current.find((x) => x.id === id);
    if (!g) return;
    setOpenById((prev) => {
      const m = prev[id];
      const currentlyOpen =
        m === false ? false : m === true ? true : sidebarGroupHasActiveChild(pathname, g.items, search);
      return { ...prev, [id]: !currentlyOpen };
    });
  }, [pathname, search]);

  return (
    <div className="app-sidebar__collapsible-root">
      {groups.map((group) => {
        const open = isGroupOpen(group);
        const hasActive = sidebarGroupHasActiveChild(pathname, group.items, search);
        const panelId = `sidebar-group-${group.id}`;
        const GroupIcon = group.icon || group.items?.[0]?.icon;
        return (
          <div key={group.id} className={`app-sidebar__collapsible app-sidebar__collapsible--${group.id}`}>
            <button
              type="button"
              id={`${panelId}-trigger`}
              className={`app-sidebar__collapsible-trigger${hasActive ? " app-sidebar__collapsible-trigger--has-active" : ""}${group.boldHeading ? " app-sidebar__collapsible-trigger--bold" : ""}`}
              aria-expanded={open}
              aria-controls={panelId}
              onClick={() => toggle(group.id)}
            >
              {GroupIcon ? <GroupIcon className="app-sidebar__icon" aria-hidden /> : null}
              <span className="app-sidebar__collapsible-heading">{group.heading}</span>
              <FaChevronDown className={`app-sidebar__collapsible-chevron${open ? " app-sidebar__collapsible-chevron--open" : ""}`} aria-hidden />
            </button>
            <div
              id={panelId}
              role="region"
              aria-labelledby={`${panelId}-trigger`}
              aria-hidden={!open}
              className={`app-sidebar__collapsible-panel${open ? " app-sidebar__collapsible-panel--open" : ""}`}
            >
              <div className="app-sidebar__collapsible-panel-inner">
                {group.items.map((item) => (
                  <SidebarNavLink key={item.id} item={item} pathname={pathname} search={search} />
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Role-aware left navigation: sections and items come from `config/sidebarNavigation.js`.
 */
export default function AuthenticatedSidebar() {
  const location = useLocation();
  const [navEpoch, setNavEpoch] = useState(0);
  const [p2pNav, setP2pNav] = useState(null);

  const isAuthed = StorageService.isAuthenticated();
  const user = StorageService.getUser();

  useEffect(() => {
    const bump = () => setNavEpoch((n) => n + 1);
    window.addEventListener("storage", bump);
    return () => window.removeEventListener("storage", bump);
  }, []);

  useEffect(() => {
    if (!isAuthed) {
      setP2pNav(null);
      return;
    }
    let cancelled = false;
    p2pNavigationService
      .getContext()
      .then((res) => {
        if (!cancelled) setP2pNav(res?.data || null);
      })
      .catch(() => {
        if (!cancelled) setP2pNav(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthed, navEpoch]);

  const roles = getAvailableRoles();
  const currentRole = getCurrentRole();
  const sections = getSidebarSections({ roles, user, currentRole, p2pNav });

  if (!isAuthed) {
    return null;
  }

  return (
    <aside className="app-sidebar" aria-label="Main navigation">
      <nav key={navEpoch} className="app-sidebar__inner">
        {sections.map((section) => {
          const highlighted = sidebarSectionIsHighlighted(section, currentRole, location.pathname);
          return (
          <div
            key={section.id}
            className={`app-sidebar__section${highlighted ? " app-sidebar__section--active" : ""}`}
          >
            {section.title ? (
              <div
                className={`app-sidebar__section-title${highlighted ? " app-sidebar__section-title--active" : ""}`}
                role="presentation"
              >
                {section.title}
              </div>
            ) : null}

            {(section.items || []).map((item) => (
              <SidebarNavLink
                key={item.id}
                item={item}
                pathname={location.pathname}
                search={location.search}
              />
            ))}

            {section.id === "platform" ? <div className="app-sidebar__divider" aria-hidden /> : null}

            {(section.topItems || []).map((item) => (
              <SidebarNavLink
                key={item.id}
                item={item}
                pathname={location.pathname}
                search={location.search}
              />
            ))}

            {section.collapsibleGroups?.length ? (
              <SidebarCollapsibleGroups
                groups={section.collapsibleGroups}
                pathname={location.pathname}
                search={location.search}
              />
            ) : null}
          </div>
          );
        })}
      </nav>
    </aside>
  );
}
